import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { InlineMediaPicker } from "./InlineMediaPicker";
import { toast } from "sonner";
import {
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Columns2,
  Columns3,
  Copy,
  CopyPlus,
  Image as ImageIcon,
  LayoutGrid,
  Redo2,
  Rows3,
  Save,
  Settings2,
  Sparkles,
  Square,
  Star,
  Trash2,
  Type as TypeIcon,
  Undo2,
  X,
  Monitor,
  Tablet,
  Smartphone,
  Rocket,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  listRichProducts,
  updateShopSettings,
  type BuilderRow,
  type BuilderColumn,
  type BuilderWidget,
  type BuilderWidgetType,
  type RichProduct,
  type ShopSettings,
} from "@/lib/products";
import { WidgetRenderer } from "./WidgetRenderer";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}
function reidWidget(w: BuilderWidget): BuilderWidget {
  return { ...clone(w), id: uid() };
}
function reidRow(r: BuilderRow): BuilderRow {
  return {
    id: uid(),
    columns: r.columns.map((c) => ({
      id: uid(),
      span: c.span,
      widgets: c.widgets.map(reidWidget),
    })),
  };
}

const WIDGET_TEMPLATES: {
  type: BuilderWidgetType;
  label: string;
  icon: React.ReactNode;
  defaults: Record<string, unknown>;
}[] = [
  {
    type: "hero",
    label: "Hero",
    icon: <Sparkles className="h-4 w-4" />,
    defaults: {
      title: "Premium custom print",
      subtitle: "Made for your brand",
      ctaText: "Browse products",
      radius: 32,
    },
  },
  {
    type: "featured",
    label: "Featured products",
    icon: <Star className="h-4 w-4" />,
    defaults: { productIds: [], layout: "grid" },
  },
  {
    type: "product_grid",
    label: "Product grid",
    icon: <LayoutGrid className="h-4 w-4" />,
    defaults: { limit: 12 },
  },
  {
    type: "heading",
    label: "Heading",
    icon: <TypeIcon className="h-4 w-4" />,
    defaults: { text: "Section heading", level: 2, align: "left" },
  },
  {
    type: "text",
    label: "Text",
    icon: <TypeIcon className="h-4 w-4" />,
    defaults: { text: "Tell your customers something useful." },
  },
  {
    type: "image",
    label: "Image",
    icon: <ImageIcon className="h-4 w-4" />,
    defaults: { url: "", rounded: true },
  },
  {
    type: "cta",
    label: "CTA banner",
    icon: <Sparkles className="h-4 w-4" />,
    defaults: {
      title: "Ready to print?",
      subtitle: "Upload your artwork in minutes.",
      buttonText: "Start a job",
      buttonHref: "/app/customer/new-job",
    },
  },
  {
    type: "spacer",
    label: "Spacer",
    icon: <Square className="h-4 w-4" />,
    defaults: { height: 32 },
  },
];

const COLUMN_PRESETS: { label: string; spans: number[]; icon: React.ReactNode }[] = [
  { label: "1 col", spans: [12], icon: <Square className="h-4 w-4" /> },
  { label: "2 col", spans: [6, 6], icon: <Columns2 className="h-4 w-4" /> },
  { label: "3 col", spans: [4, 4, 4], icon: <Columns3 className="h-4 w-4" /> },
  { label: "8 + 4", spans: [8, 4], icon: <Columns2 className="h-4 w-4" /> },
  { label: "4 + 8", spans: [4, 8], icon: <Columns2 className="h-4 w-4" /> },
];

interface Props {
  settings: ShopSettings | undefined;
  liveProducts: RichProduct[];
  onExit: () => void;
}

export function InlineShopBuilder({ settings, liveProducts, onExit }: Props) {
  const qc = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { data: allProducts = [] } = useQuery<RichProduct[]>({
    queryKey: ["rich-products"],
    queryFn: listRichProducts,
  });

  const [rows, setRowsRaw] = useState<BuilderRow[]>([]);
  const [useBuilder, setUseBuilder] = useState(false);
  const hydratedRef = useRef(false);
  useEffect(() => {
    // Hydrate state once settings arrive (the editor can mount before the query resolves).
    if (hydratedRef.current) return;
    if (!settings) return;
    if (Array.isArray(settings.builder_rows)) {
      setRowsRaw(settings.builder_rows as BuilderRow[]);
    }
    setUseBuilder(!!settings.use_builder);
    hydratedRef.current = true;
  }, [settings]);
  const [selected, setSelected] = useState<{
    rowId: string;
    colId: string;
    widgetId: string;
  } | null>(null);
  const [showMediaFor, setShowMediaFor] = useState<{ prop: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [confirmGoLive, setConfirmGoLive] = useState(false);
  const [device, setDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [confirmPublish, setConfirmPublish] = useState(false);

  // ─── History ──────────────────────────────────────────────
  const past = useRef<BuilderRow[][]>([]);
  const future = useRef<BuilderRow[][]>([]);
  const [, tick] = useState(0);

  const commit = useCallback((updater: (prev: BuilderRow[]) => BuilderRow[]) => {
    setRowsRaw((prev) => {
      const next = updater(prev);
      past.current.push(clone(prev));
      if (past.current.length > 100) past.current.shift();
      future.current = [];
      tick((t) => t + 1);
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    setRowsRaw((curr) => {
      const prev = past.current.pop()!;
      future.current.push(clone(curr));
      tick((t) => t + 1);
      return prev;
    });
  }, []);

  const redo = useCallback(() => {
    if (!future.current.length) return;
    setRowsRaw((curr) => {
      const next = future.current.pop()!;
      past.current.push(clone(curr));
      tick((t) => t + 1);
      return next;
    });
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  const selectedWidget = useMemo<BuilderWidget | null>(() => {
    if (!selected) return null;
    const row = rows.find((r) => r.id === selected.rowId);
    const col = row?.columns.find((c) => c.id === selected.colId);
    return col?.widgets.find((w) => w.id === selected.widgetId) ?? null;
  }, [selected, rows]);

  // ─── Mutators ─────────────────────────────────────────────
  const addRow = (spans: number[] = [12]) => {
    const row: BuilderRow = {
      id: uid(),
      columns: spans.map((s) => ({ id: uid(), span: s, widgets: [] })),
    };
    commit((r) => [...r, row]);
  };
  const deleteRow = (rowId: string) => {
    commit((r) => r.filter((x) => x.id !== rowId));
    if (selected?.rowId === rowId) setSelected(null);
  };
  const duplicateRow = (rowId: string) => {
    commit((rs) => {
      const i = rs.findIndex((r) => r.id === rowId);
      if (i < 0) return rs;
      const copy = reidRow(rs[i]);
      const next = [...rs];
      next.splice(i + 1, 0, copy);
      return next;
    });
    toast.success("Row duplicated");
  };
  const moveRow = (rowId: string, dir: -1 | 1) => {
    commit((r) => {
      const i = r.findIndex((x) => x.id === rowId);
      if (i < 0) return r;
      const j = i + dir;
      if (j < 0 || j >= r.length) return r;
      const next = [...r];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const setRowSpans = (rowId: string, spans: number[]) => {
    commit((rs) =>
      rs.map((r) => {
        if (r.id !== rowId) return r;
        const next: BuilderColumn[] = spans.map((span, i) => {
          const existing = r.columns[i];
          return existing ? { ...existing, span } : { id: uid(), span, widgets: [] };
        });
        if (r.columns.length > spans.length) {
          const overflow = r.columns.slice(spans.length).flatMap((c) => c.widgets);
          next[next.length - 1] = {
            ...next[next.length - 1],
            widgets: [...next[next.length - 1].widgets, ...overflow],
          };
        }
        return { ...r, columns: next };
      }),
    );
  };
  const addWidget = (rowId: string, colId: string, type: BuilderWidgetType) => {
    const tmpl = WIDGET_TEMPLATES.find((t) => t.type === type)!;
    const widget: BuilderWidget = { id: uid(), type, props: { ...tmpl.defaults } };
    commit((rs) =>
      rs.map((r) =>
        r.id !== rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) =>
                c.id !== colId ? c : { ...c, widgets: [...c.widgets, widget] },
              ),
            },
      ),
    );
    setSelected({ rowId, colId, widgetId: widget.id });
  };
  const deleteWidget = (rowId: string, colId: string, widgetId: string) => {
    commit((rs) =>
      rs.map((r) =>
        r.id !== rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) =>
                c.id !== colId ? c : { ...c, widgets: c.widgets.filter((w) => w.id !== widgetId) },
              ),
            },
      ),
    );
    if (selected?.widgetId === widgetId) setSelected(null);
  };
  const duplicateWidget = (rowId: string, colId: string, widgetId: string) => {
    let newId = "";
    commit((rs) =>
      rs.map((r) =>
        r.id !== rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) => {
                if (c.id !== colId) return c;
                const idx = c.widgets.findIndex((w) => w.id === widgetId);
                if (idx < 0) return c;
                const copy = reidWidget(c.widgets[idx]);
                newId = copy.id;
                const widgets = [...c.widgets];
                widgets.splice(idx + 1, 0, copy);
                return { ...c, widgets };
              }),
            },
      ),
    );
    if (newId) setSelected({ rowId, colId, widgetId: newId });
    toast.success("Widget duplicated");
  };
  const updateWidgetProps = (patch: Record<string, unknown>) => {
    if (!selected) return;
    commit((rs) =>
      rs.map((r) =>
        r.id !== selected.rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) =>
                c.id !== selected.colId
                  ? c
                  : {
                      ...c,
                      widgets: c.widgets.map((w) =>
                        w.id !== selected.widgetId ? w : { ...w, props: { ...w.props, ...patch } },
                      ),
                    },
              ),
            },
      ),
    );
  };

  // ─── DnD ──────────────────────────────────────────────────
  const findWidget = useCallback((widgetId: string, rs: BuilderRow[]) => {
    for (const r of rs)
      for (const c of r.columns) {
        const i = c.widgets.findIndex((w) => w.id === widgetId);
        if (i >= 0) return { rowId: r.id, colId: c.id, index: i };
      }
    return null;
  }, []);

  const onDragEnd = (e: DragEndEvent) => {
    const activeRaw = String(e.active.id);
    const overRaw = e.over?.id ? String(e.over.id) : null;
    if (!overRaw) return;
    const [, , , widgetId] = activeRaw.split(":");
    commit((rs) => {
      const src = findWidget(widgetId, rs);
      if (!src) return rs;
      let dstRowId: string, dstColId: string, dstIndex: number;
      if (overRaw.startsWith("w:")) {
        const [, , , overWidgetId] = overRaw.split(":");
        if (overWidgetId === widgetId) return rs;
        const dst = findWidget(overWidgetId, rs);
        if (!dst) return rs;
        dstRowId = dst.rowId;
        dstColId = dst.colId;
        dstIndex = dst.index;
      } else if (overRaw.startsWith("drop:")) {
        const [, rId, cId] = overRaw.split(":");
        dstRowId = rId;
        dstColId = cId;
        const col = rs.find((r) => r.id === rId)?.columns.find((c) => c.id === cId);
        dstIndex = col?.widgets.length ?? 0;
      } else return rs;

      if (src.rowId === dstRowId && src.colId === dstColId) {
        return rs.map((r) =>
          r.id !== src.rowId
            ? r
            : {
                ...r,
                columns: r.columns.map((c) =>
                  c.id !== src.colId
                    ? c
                    : { ...c, widgets: arrayMove(c.widgets, src.index, dstIndex) },
                ),
              },
        );
      }
      let moved: BuilderWidget | null = null;
      const stripped = rs.map((r) =>
        r.id !== src.rowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) => {
                if (c.id !== src.colId) return c;
                const widgets = [...c.widgets];
                moved = widgets.splice(src.index, 1)[0] ?? null;
                return { ...c, widgets };
              }),
            },
      );
      if (!moved) return rs;
      return stripped.map((r) =>
        r.id !== dstRowId
          ? r
          : {
              ...r,
              columns: r.columns.map((c) => {
                if (c.id !== dstColId) return c;
                const widgets = [...c.widgets];
                widgets.splice(Math.min(dstIndex, widgets.length), 0, moved as BuilderWidget);
                return { ...c, widgets };
              }),
            },
      );
    });
  };

  const handleSave = async (nextUseBuilder = useBuilder) => {
    setSaving(true);
    try {
      await updateShopSettings({ builder_rows: rows, use_builder: nextUseBuilder });
      setUseBuilder(nextUseBuilder);
      await qc.invalidateQueries({ queryKey: ["shop-settings"] });
      await qc.refetchQueries({ queryKey: ["shop-settings"] });
      toast.success(nextUseBuilder ? "Published live to customers" : "Draft saved");
    } catch (err) {
      toast.error((err as Error).message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => setConfirmPublish(true);

  const onToggleLive = (v: boolean) => {
    if (v && !useBuilder) setConfirmGoLive(true);
    else setUseBuilder(v);
  };

  const ctx = { liveProducts };
  const canUndo = past.current.length > 0;
  const canRedo = future.current.length > 0;

  const deviceWidth = device === "mobile" ? 420 : device === "tablet" ? 820 : null;

  return (
    <div className="relative">
      <AlertDialog open={confirmGoLive} onOpenChange={setConfirmGoLive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish builder layout to customers?</AlertDialogTitle>
            <AlertDialogDescription>
              Customers will immediately see the builder canvas instead of the old shop layout. You
              can switch back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep as draft</AlertDialogCancel>
            <AlertDialogAction onClick={() => setUseBuilder(true)}>
              Yes, publish it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmPublish} onOpenChange={setConfirmPublish}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish changes to live shop?</AlertDialogTitle>
            <AlertDialogDescription>
              This saves the current layout and switches the customer shop to the builder view.
              Changes appear immediately for shoppers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmPublish(false);
                handleSave(true);
              }}
            >
              Publish now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating top toolbar */}
      <div className="sticky top-2 z-30 mb-4 flex flex-wrap items-center justify-between gap-2 rounded-full border border-border bg-card/90 px-3 py-1.5 backdrop-blur shadow-soft">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <LayoutGrid className="h-4 w-4 text-primary" /> Editing shop page
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="flex items-center rounded-full border border-border bg-background p-0.5 mr-1">
            {(
              [
                { id: "desktop", icon: <Monitor className="h-3.5 w-3.5" />, label: "Desktop" },
                { id: "tablet", icon: <Tablet className="h-3.5 w-3.5" />, label: "Tablet" },
                { id: "mobile", icon: <Smartphone className="h-3.5 w-3.5" />, label: "Mobile" },
              ] as const
            ).map((d) => (
              <button
                key={d.id}
                onClick={() => setDevice(d.id)}
                title={d.label}
                className={cn(
                  "flex items-center gap-1 rounded-full px-2 py-1 text-[11px] transition-colors",
                  device === d.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {d.icon}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={undo}
            disabled={!canUndo}
            className="rounded-full gap-1 h-8"
          >
            <Undo2 className="h-3.5 w-3.5" /> Undo
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={redo}
            disabled={!canRedo}
            className="rounded-full gap-1 h-8"
          >
            <Redo2 className="h-3.5 w-3.5" /> Redo
          </Button>
          <label className="flex items-center gap-2 text-xs text-muted-foreground pl-2 border-l border-border ml-1">
            <Switch checked={useBuilder} onCheckedChange={onToggleLive} />
            {useBuilder ? "Live to customers" : "Draft"}
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="rounded-full gap-1.5 h-8"
          >
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save draft"}
          </Button>
          <Button
            size="sm"
            onClick={handlePublish}
            disabled={saving}
            className="rounded-full gap-1.5"
          >
            <Rocket className="h-3.5 w-3.5" /> Publish
          </Button>
          <Button size="sm" variant="ghost" onClick={onExit} className="rounded-full gap-1 h-8">
            <X className="h-3.5 w-3.5" /> Done
          </Button>
        </div>
      </div>

      {/* Floating palette (right side, collapsible) */}
      <div
        className={cn(
          "fixed top-24 right-4 z-30 flex flex-col gap-2",
          paletteOpen ? "w-64" : "w-12",
        )}
      >
        <button
          onClick={() => setPaletteOpen((v) => !v)}
          className="self-end rounded-full border border-border bg-card p-1.5 shadow-soft hover:border-primary/40"
          title={paletteOpen ? "Hide palette" : "Show palette"}
        >
          {paletteOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
        {paletteOpen && (
          <div className="rounded-2xl border border-border bg-card/95 backdrop-blur p-3 shadow-soft space-y-3 max-h-[70vh] overflow-y-auto">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Add a row
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {COLUMN_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => addRow(p.spans)}
                    className="flex items-center gap-1.5 rounded-lg border border-border bg-background p-1.5 text-[11px] hover:border-primary/40"
                  >
                    {p.icon}
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Widgets
              </div>
              <p className="text-[10px] text-muted-foreground mb-1.5">
                Select a column, then click a widget.
              </p>
              <div className="space-y-1">
                {WIDGET_TEMPLATES.map((t) => (
                  <button
                    key={t.type}
                    onClick={() => {
                      if (selected) {
                        addWidget(selected.rowId, selected.colId, t.type);
                        return;
                      }
                      const rowId = uid();
                      const colId = uid();
                      const widget: BuilderWidget = {
                        id: uid(),
                        type: t.type,
                        props: { ...t.defaults },
                      };
                      commit((r) => [
                        ...r,
                        { id: rowId, columns: [{ id: colId, span: 12, widgets: [widget] }] },
                      ]);
                      setSelected({ rowId, colId, widgetId: widget.id });
                    }}
                    className="flex w-full items-center gap-2 rounded-lg border border-border bg-background p-1.5 text-xs hover:border-primary/40"
                  >
                    <span className="text-primary">{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {selectedWidget && (
              <div className="border-t border-border pt-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Settings2 className="h-3 w-3" /> {selectedWidget.type} settings
                </div>
                <WidgetSettings
                  widget={selectedWidget}
                  allProducts={allProducts}
                  onChange={updateWidgetProps}
                  onPickMedia={(prop) =>
                    setShowMediaFor((cur) => (cur?.prop === prop ? null : { prop }))
                  }
                  mediaPickerFor={showMediaFor?.prop ?? null}
                  onMediaSelected={(prop, url) => {
                    updateWidgetProps({ [prop]: url });
                    setShowMediaFor(null);
                  }}
                  onCloseMediaPicker={() => setShowMediaFor(null)}
                  onDuplicate={() =>
                    selected && duplicateWidget(selected.rowId, selected.colId, selected.widgetId)
                  }
                  onDelete={() =>
                    selected && deleteWidget(selected.rowId, selected.colId, selected.widgetId)
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inline canvas — renders directly on the shop page */}
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-24 rounded-3xl border-2 border-dashed border-border">
            <Rows3 className="h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">Empty page</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add a row from the palette on the right to start designing.
            </p>
          </div>
        ) : (
          <div
            className={cn("space-y-4 pr-[17rem]", deviceWidth && "mx-auto pr-0 transition-all")}
            style={deviceWidth ? { maxWidth: deviceWidth } : undefined}
          >
            {rows.map((row, ri) => (
              <RowBlock
                key={row.id}
                row={row}
                index={ri}
                total={rows.length}
                selected={selected}
                ctx={ctx}
                onSelect={(colId, widgetId) => setSelected({ rowId: row.id, colId, widgetId })}
                onSelectColumn={(colId) => setSelected({ rowId: row.id, colId, widgetId: "" })}
                onDeleteWidget={(colId, widgetId) => deleteWidget(row.id, colId, widgetId)}
                onDuplicateWidget={(colId, widgetId) => duplicateWidget(row.id, colId, widgetId)}
                onDeleteRow={() => deleteRow(row.id)}
                onDuplicateRow={() => duplicateRow(row.id)}
                onMoveRow={(d) => moveRow(row.id, d)}
                onChangeSpans={(spans) => setRowSpans(row.id, spans)}
              />
            ))}
          </div>
        )}
      </DndContext>
    </div>
  );
}

// ─── Row / column / widget ────────────────────────────────────────────────────

function RowBlock({
  row,
  index,
  total,
  selected,
  ctx,
  onSelect,
  onSelectColumn,
  onDeleteWidget,
  onDuplicateWidget,
  onDeleteRow,
  onDuplicateRow,
  onMoveRow,
  onChangeSpans,
}: {
  row: BuilderRow;
  index: number;
  total: number;
  selected: { rowId: string; colId: string; widgetId: string } | null;
  ctx: { liveProducts: RichProduct[] };
  onSelect: (colId: string, widgetId: string) => void;
  onSelectColumn: (colId: string) => void;
  onDeleteWidget: (colId: string, widgetId: string) => void;
  onDuplicateWidget: (colId: string, widgetId: string) => void;
  onDeleteRow: () => void;
  onDuplicateRow: () => void;
  onMoveRow: (dir: -1 | 1) => void;
  onChangeSpans: (spans: number[]) => void;
}) {
  return (
    <div className="group/row relative rounded-2xl border border-dashed border-transparent hover:border-primary/30 p-2 transition-colors">
      <div className="absolute -top-3 left-3 z-10 flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground shadow-soft opacity-0 group-hover/row:opacity-100 transition-opacity">
        <span>Row {index + 1}</span>
      </div>
      <div className="absolute -top-3 right-3 z-10 flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
        {COLUMN_PRESETS.map((preset) => (
          <button
            key={preset.label}
            onClick={() => onChangeSpans(preset.spans)}
            title={preset.label}
            className={cn(
              "rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground hover:border-primary/40 shadow-soft",
              JSON.stringify(row.columns.map((c) => c.span)) === JSON.stringify(preset.spans) &&
                "border-primary text-primary",
            )}
          >
            {preset.icon}
          </button>
        ))}
        <button
          onClick={() => onMoveRow(-1)}
          disabled={index === 0}
          title="Move up"
          className="rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground disabled:opacity-40 shadow-soft"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onMoveRow(1)}
          disabled={index === total - 1}
          title="Move down"
          className="rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground disabled:opacity-40 shadow-soft"
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onDuplicateRow}
          title="Copy row"
          className="rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground hover:border-primary/40 shadow-soft"
        >
          <CopyPlus className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onDeleteRow}
          title="Delete row"
          className="rounded-full border border-border bg-background p-1 text-destructive hover:bg-destructive/10 shadow-soft"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-12 gap-3">
        {row.columns.map((col) => (
          <ColumnBlock
            key={col.id}
            row={row}
            col={col}
            selected={selected}
            ctx={ctx}
            onSelect={(widgetId) => onSelect(col.id, widgetId)}
            onSelectEmpty={() => onSelectColumn(col.id)}
            onDeleteWidget={(widgetId) => onDeleteWidget(col.id, widgetId)}
            onDuplicateWidget={(widgetId) => onDuplicateWidget(col.id, widgetId)}
          />
        ))}
      </div>
    </div>
  );
}

function ColumnBlock({
  row,
  col,
  selected,
  ctx,
  onSelect,
  onSelectEmpty,
  onDeleteWidget,
  onDuplicateWidget,
}: {
  row: BuilderRow;
  col: BuilderColumn;
  selected: { rowId: string; colId: string; widgetId: string } | null;
  ctx: { liveProducts: RichProduct[] };
  onSelect: (widgetId: string) => void;
  onSelectEmpty: () => void;
  onDeleteWidget: (widgetId: string) => void;
  onDuplicateWidget: (widgetId: string) => void;
}) {
  const dropId = `drop:${row.id}:${col.id}`;
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  const isSelCol = selected?.rowId === row.id && selected?.colId === col.id && !selected?.widgetId;
  const sortableIds = col.widgets.map((w) => `w:${row.id}:${col.id}:${w.id}`);
  return (
    <div
      ref={setNodeRef}
      onClick={(e) => {
        if (col.widgets.length === 0) {
          e.stopPropagation();
          onSelectEmpty();
        }
      }}
      className={cn(
        "min-h-[80px] rounded-xl border border-dashed p-2 space-y-3 transition-colors",
        isOver ? "border-primary bg-primary/5" : "border-border/40 hover:border-border",
        isSelCol && "border-primary/60 bg-primary/5",
      )}
      style={{ gridColumn: `span ${col.span} / span ${col.span}` }}
    >
      {col.widgets.length === 0 && (
        <div className="flex h-full min-h-[60px] items-center justify-center text-[11px] text-muted-foreground">
          Empty column — click then add a widget
        </div>
      )}
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        {col.widgets.map((widget) => (
          <SortableWidget
            key={widget.id}
            row={row}
            col={col}
            widget={widget}
            ctx={ctx}
            isSelected={selected?.widgetId === widget.id}
            onSelect={() => onSelect(widget.id)}
            onDelete={() => onDeleteWidget(widget.id)}
            onDuplicate={() => onDuplicateWidget(widget.id)}
          />
        ))}
      </SortableContext>
    </div>
  );
}

function SortableWidget({
  row,
  col,
  widget,
  ctx,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
}: {
  row: BuilderRow;
  col: BuilderColumn;
  widget: BuilderWidget;
  ctx: { liveProducts: RichProduct[] };
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const dragId = `w:${row.id}:${col.id}:${widget.id}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dragId,
  });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group/widget relative rounded-xl transition-shadow",
        isSelected
          ? "outline outline-2 outline-primary outline-offset-2"
          : "hover:outline hover:outline-1 hover:outline-primary/40 hover:outline-offset-2",
        isDragging && "opacity-60 shadow-lg z-20",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <div className="absolute -top-3 left-3 z-10 flex items-center gap-1 rounded-full border border-border bg-background px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground shadow-soft opacity-0 group-hover/widget:opacity-100 transition-opacity">
        <span {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing px-0.5">
          ⋮⋮
        </span>
        <span>{widget.type}</span>
      </div>
      <div className="absolute -top-3 right-3 z-10 flex items-center gap-1 opacity-0 group-hover/widget:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          title="Duplicate"
          className="rounded-full border border-border bg-background p-1 text-muted-foreground hover:text-foreground hover:border-primary/40 shadow-soft"
        >
          <Copy className="h-3 w-3" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete"
          className="rounded-full border border-border bg-background p-1 text-destructive hover:bg-destructive/10 shadow-soft"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <div className="pointer-events-none">
        <WidgetRenderer widget={widget} ctx={ctx} />
      </div>
    </div>
  );
}

// ─── Settings panel (compact, in the floating palette) ──────────────────────

function WidgetSettings({
  widget,
  allProducts,
  onChange,
  onPickMedia,
  mediaPickerFor,
  onMediaSelected,
  onCloseMediaPicker,
  onDuplicate,
  onDelete,
}: {
  widget: BuilderWidget;
  allProducts: RichProduct[];
  onChange: (patch: Record<string, unknown>) => void;
  onPickMedia: (prop: string) => void;
  mediaPickerFor: string | null;
  onMediaSelected: (prop: string, url: string) => void;
  onCloseMediaPicker: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const p = widget.props ?? {};
  const renderPicker = (prop: string, currentUrl?: string | null) =>
    mediaPickerFor === prop ? (
      <div className="mt-2">
        <InlineMediaPicker
          value={currentUrl ?? null}
          onSelect={(url) => onMediaSelected(prop, url)}
          onClose={onCloseMediaPicker}
        />
      </div>
    ) : null;
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between text-xs">
        <button onClick={onDuplicate} className="flex items-center gap-1 hover:underline">
          <Copy className="h-3 w-3" /> Duplicate
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 text-destructive hover:underline"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>

      {widget.type === "hero" && (
        <>
          <Field label="Title">
            <Input value={p.title ?? ""} onChange={(e) => onChange({ title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <Input
              value={p.subtitle ?? ""}
              onChange={(e) => onChange({ subtitle: e.target.value })}
            />
          </Field>
          <Field label="CTA text">
            <Input
              value={p.ctaText ?? ""}
              onChange={(e) => onChange({ ctaText: e.target.value })}
            />
          </Field>
          <Field label="CTA link">
            <Input
              value={p.ctaHref ?? ""}
              onChange={(e) => onChange({ ctaHref: e.target.value })}
              placeholder="/app/customer/shop"
            />
          </Field>
          <Field label="Background image">
            <div className="flex items-center gap-2">
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt=""
                  className="h-8 w-12 rounded object-cover border border-border"
                />
              )}
              <Button size="sm" variant="outline" onClick={() => onPickMedia("imageUrl")}>
                {mediaPickerFor === "imageUrl" ? "Close" : p.imageUrl ? "Change" : "Choose"}
              </Button>
              {p.imageUrl && (
                <button
                  onClick={() => onChange({ imageUrl: null })}
                  className="text-xs text-muted-foreground underline"
                >
                  Remove
                </button>
              )}
            </div>
            {renderPicker("imageUrl", p.imageUrl as string | undefined)}
          </Field>
          <RangeField
            label={`Min height: ${p.height ?? 0}px`}
            min={0}
            max={700}
            step={20}
            value={p.height ?? 0}
            onChange={(v) => onChange({ height: v })}
          />
          <RangeField
            label={`Radius: ${p.radius ?? 32}px`}
            min={0}
            max={60}
            step={4}
            value={p.radius ?? 32}
            onChange={(v) => onChange({ radius: v })}
          />
        </>
      )}

      {widget.type === "featured" && (
        <>
          <Field label="Layout">
            <div className="grid grid-cols-3 gap-1.5">
              {["grid", "bento", "carousel"].map((v) => (
                <button
                  key={v}
                  onClick={() => onChange({ layout: v })}
                  className={cn(
                    "rounded-lg border py-1 text-xs",
                    p.layout === v ? "border-primary text-primary bg-primary/5" : "border-border",
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Products">
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
              {allProducts.map((prod) => {
                const sel = Array.isArray(p.productIds) && p.productIds.includes(prod.id);
                return (
                  <button
                    key={prod.id}
                    onClick={() => {
                      const ids: string[] = Array.isArray(p.productIds) ? [...p.productIds] : [];
                      const i = ids.indexOf(prod.id);
                      if (i >= 0) ids.splice(i, 1);
                      else ids.push(prod.id);
                      onChange({ productIds: ids });
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md p-1.5 text-left text-xs",
                      sel ? "bg-primary/10 text-primary" : "hover:bg-muted",
                    )}
                  >
                    {prod.image_main_url ? (
                      <img
                        src={prod.image_main_url}
                        alt=""
                        className="h-6 w-6 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="h-6 w-6 shrink-0 rounded bg-muted" />
                    )}
                    <span className="truncate">{prod.name}</span>
                  </button>
                );
              })}
              {allProducts.length === 0 && (
                <p className="text-xs text-muted-foreground">No products yet.</p>
              )}
            </div>
          </Field>
        </>
      )}

      {widget.type === "product_grid" && (
        <RangeField
          label={`Show up to: ${p.limit ?? 12}`}
          min={4}
          max={48}
          step={4}
          value={p.limit ?? 12}
          onChange={(v) => onChange({ limit: v })}
        />
      )}

      {widget.type === "heading" && (
        <>
          <Field label="Text">
            <Input value={p.text ?? ""} onChange={(e) => onChange({ text: e.target.value })} />
          </Field>
          <Field label="Level">
            <div className="grid grid-cols-4 gap-1">
              {[1, 2, 3, 4].map((lv) => (
                <button
                  key={lv}
                  onClick={() => onChange({ level: lv })}
                  className={cn(
                    "rounded-lg border py-1 text-xs",
                    (p.level ?? 2) === lv
                      ? "border-primary text-primary bg-primary/5"
                      : "border-border",
                  )}
                >
                  H{lv}
                </button>
              ))}
            </div>
          </Field>
          <AlignField value={p.align ?? "left"} onChange={(v) => onChange({ align: v })} />
        </>
      )}

      {widget.type === "text" && (
        <>
          <Field label="Text">
            <textarea
              value={p.text ?? ""}
              onChange={(e) => onChange({ text: e.target.value })}
              rows={5}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </Field>
          <AlignField value={p.align ?? "left"} onChange={(v) => onChange({ align: v })} />
        </>
      )}

      {widget.type === "image" && (
        <>
          <Field label="Image">
            <div className="flex items-center gap-2">
              {p.url && (
                <img
                  src={p.url}
                  alt=""
                  className="h-8 w-12 rounded object-cover border border-border"
                />
              )}
              <Button size="sm" variant="outline" onClick={() => onPickMedia("url")}>
                {mediaPickerFor === "url" ? "Close" : p.url ? "Change" : "Choose"}
              </Button>
            </div>
            {renderPicker("url", p.url as string | undefined)}
          </Field>
          <Field label="Alt text">
            <Input value={p.alt ?? ""} onChange={(e) => onChange({ alt: e.target.value })} />
          </Field>
          <Field label="Rounded">
            <Switch checked={p.rounded ?? true} onCheckedChange={(v) => onChange({ rounded: v })} />
          </Field>
        </>
      )}

      {widget.type === "cta" && (
        <>
          <Field label="Title">
            <Input value={p.title ?? ""} onChange={(e) => onChange({ title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <Input
              value={p.subtitle ?? ""}
              onChange={(e) => onChange({ subtitle: e.target.value })}
            />
          </Field>
          <Field label="Button text">
            <Input
              value={p.buttonText ?? ""}
              onChange={(e) => onChange({ buttonText: e.target.value })}
            />
          </Field>
          <Field label="Button link">
            <Input
              value={p.buttonHref ?? ""}
              onChange={(e) => onChange({ buttonHref: e.target.value })}
            />
          </Field>
        </>
      )}

      {widget.type === "spacer" && (
        <RangeField
          label={`Height: ${p.height ?? 32}px`}
          min={8}
          max={240}
          step={8}
          value={p.height ?? 32}
          onChange={(v) => onChange({ height: v })}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-[11px] font-medium text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function RangeField({
  label,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={label}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
      />
    </Field>
  );
}
function AlignField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Alignment">
      <div className="grid grid-cols-3 gap-1">
        {["left", "center", "right"].map((a) => (
          <button
            key={a}
            onClick={() => onChange(a)}
            className={cn(
              "rounded-lg border py-1 text-xs capitalize",
              value === a ? "border-primary text-primary bg-primary/5" : "border-border",
            )}
          >
            {a}
          </button>
        ))}
      </div>
    </Field>
  );
}
